import { useState, useEffect, useCallback } from 'react';
import {
    collection,
    query,
    where,
    orderBy,
    limit,
    startAfter,
    getDocs,
    QueryDocumentSnapshot,
    onSnapshot,
} from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { Participant } from '../types';

const PAGE_SIZE = 50;

/**
 * useParticipants — Paginated participant fetching hook.
 *
 * Replaces the unbounded onSnapshot for participants in AppDataContext.
 * MissionControlView's useMemo filter logic is preserved — it now operates
 * on a paginated slice instead of the full dataset.
 *
 * Usage:
 *   const { participants, fetchNextPage, hasMore, loading } = useParticipants(projectId, claimsOrgId, claimsRole);
 */
export function useParticipants(projectId: string, organizationId: string | null, claimsRole: string | null) {
    const [participants, setParticipants] = useState<Participant[]>([]);
    const [cursor, setCursor] = useState<QueryDocumentSnapshot | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchPage = useCallback(async (reset = false) => {
        if (loading) return;

        // Missing scope — settle instead of spinning forever (mirrors
        // useSubmissions; these returns used to leave initialLoading true).
        if (!projectId || (!organizationId && claimsRole !== 'superadmin')) {
            setInitialLoading(false);
            return;
        }

        setLoading(true);

        try {
            const constraints: any[] = [
                where('projectId', '==', projectId),
            ];

            // Org-scoped filter — superadmins skip this to see all orgs
            if (organizationId) {
                constraints.push(where('organizationId', '==', organizationId));
            }

            constraints.push(orderBy('status', 'asc'));

            if (!reset && cursor) {
                constraints.push(startAfter(cursor));
            }

            constraints.push(limit(PAGE_SIZE));

            const q = query(collection(db, 'participants'), ...constraints);
            const snap = await getDocs(q);
            const items = snap.docs.map(d => ({ id: d.id, ...d.data() as Record<string, unknown> } as Participant));

            setParticipants(prev => reset ? items : [...prev, ...items]);
            setCursor(snap.docs[snap.docs.length - 1] ?? null);
            setHasMore(snap.docs.length === PAGE_SIZE);
            setError(null);
        } catch (err) {
            // Surface the failure — a swallowed FAILED_PRECONDITION (missing
            // composite index) once made /data render a convincing empty page.
            console.error('[useParticipants] Fetch error:', err);
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    }, [projectId, organizationId, claimsRole, cursor, loading]);

    // Reset when project changes
    useEffect(() => {
        setParticipants([]);
        setCursor(null);
        setHasMore(true);
        setInitialLoading(true);
        fetchPage(true);
    }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        participants,
        fetchNextPage: () => fetchPage(false),
        hasMore,
        loading,
        initialLoading,
        error,
        /** Total loaded count */
        count: participants.length,
    };
}
